import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../styles/ServiceStatus.css";
import ServiceStatusInfo from "../services/ServiceStatusInfo";
import Swal from "sweetalert2";

const ServiceStatus = ({ serviceName }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  console.log("serviceName" + serviceName);

  useEffect(() => {
    // Reset loading state when serviceName changes
    if (!serviceName) {
      setLoading(false);
      return;
    }

    const fetchServiceStatus = async () => {
      setLoading(true);
      try {
        const data = await ServiceStatusInfo.serviceStatusDetails(serviceName);

        // Validate data exists
        if (!data) {
          throw new Error("No data received from server");
        }

        // Safely parse values with null/undefined handling
        const usedCalls = Number(data.usedCallsForDealer) || 0;
        const totalCalls = Number(data.totalCallForDealer) || 0;
        const usedChats = Number(data.usedChatsForDealer) || 0;
        const totalChats = Number(data.totalChatsForDelaer) || 0;
        const balanceCalls = Number(data.balanceCallsForDealer) || 0;
        const balanceChats = Number(data.balanceChatsForDealer) || 0;

        // Calculate usage percentage (for reference, not used for bar display)
        const callUsagePercentage = totalCalls > 0 
          ? Math.min((usedCalls / totalCalls) * 100, 100) 
          : 0;
        const chatUsagePercentage = totalChats > 0 
          ? Math.min((usedChats / totalChats) * 100, 100) 
          : 0;
        
        // Calculate remaining balance percentage (for progress bar width and color)
        // When usage is 0%, balance is 100%, so bar should be full and green
        // Progress bar shows remaining balance, not usage
        const callBalancePercentage = totalCalls > 0 
          ? Math.min((balanceCalls / totalCalls) * 100, 100)
          : 0;
        const chatBalancePercentage = totalChats > 0 
          ? Math.min((balanceChats / totalChats) * 100, 100)
          : 0;
        
        // Per-plan breakdown (one card per plan) — falls back to the combined totals above
        // when the backend doesn't return it, so this stays backward compatible.
        const planBalances = Array.isArray(data.planBalances) && data.planBalances.length > 0
          ? data.planBalances.map((plan) => {
              const planUsedCalls = Number(plan.usedCallsForDealer) || 0;
              const planTotalCalls = Number(plan.totalCallForDealer) || 0;
              const planUsedChats = Number(plan.usedChatsForDealer) || 0;
              const planTotalChats = Number(plan.totalChatsForDelaer) || 0;
              const planBalanceCalls = Number(plan.balanceCallsForDealer) || 0;
              const planBalanceChats = Number(plan.balanceChatsForDealer) || 0;
              return {
                planId: plan.planId,
                planName: plan.planName || "N/A",
                usedCallsForDealer: planUsedCalls,
                totalCallForDealer: planTotalCalls,
                usedChatsForDealer: planUsedChats,
                totalChatsForDelaer: planTotalChats,
                balanceCallsForDealer: planBalanceCalls,
                balanceChatsForDealer: planBalanceChats,
                callBalancePercentage: planTotalCalls > 0
                  ? Math.min((planBalanceCalls / planTotalCalls) * 100, 100)
                  : 0,
                chatBalancePercentage: planTotalChats > 0
                  ? Math.min((planBalanceChats / planTotalChats) * 100, 100)
                  : 0,
              };
            })
          : null;

        const updatedData = {
          planNames: data.planNames || "N/A",
          usedCallsForDealer: usedCalls,
          totalCallForDealer: totalCalls,
          usedChatsForDealer: usedChats,
          totalChatsForDelaer: totalChats,
          balanceCallsForDealer: balanceCalls,
          balanceChatsForDealer: balanceChats,
          serviceName: data.serviceName || "",
          callPercentage: callUsagePercentage, // Usage percentage for display
          chatPercentage: chatUsagePercentage, // Usage percentage for display
          callBalancePercentage: callBalancePercentage, // Balance percentage for color
          chatBalancePercentage: chatBalancePercentage, // Balance percentage for color
          planBalances, // null when backend has no per-plan breakdown yet
        };

        setPlanStatus(updatedData);

      } catch (error) {
        console.error("Error fetching service status:", error);
        
        // Show user-friendly error message
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || error.message || "Failed to fetch service status. Please try again later.",
          confirmButtonText: "OK",
        });
        
        // Set default empty state on error
        setPlanStatus({
          planNames: "N/A",
          usedCallsForDealer: 0,
          totalCallForDealer: 0,
          usedChatsForDealer: 0,
          totalChatsForDelaer: 0,
          balanceCallsForDealer: 0,
          balanceChatsForDealer: 0,
          serviceName: "",
          callPercentage: 0,
          chatPercentage: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchServiceStatus();












    // Handle window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [serviceName]); // ✅ Fixed: Added serviceName dependency

  /* const purchasedPlan = {
     name: t("pro_plan_name"),
     connectedCallMinutes: 45,
     totalCallMinutes: 99,
     deliveredChats: 70,
     totalChats: 99,
   };
 
  
 
   const purchasedPlan= {
     planName:'SSs',
     connectedCallMinutes:45,
     totalCallMinutes:99,
     deliveredChats:70,
     totalChats:99
   } ;
 */
  // const callPercentage = (connectedCallMinutes / totalCallMinutes) * 100;
  // const chatPercentage = (deliveredChats / totalChats) * 100;

  const getProgressBarColor = (percentage) => {
    // Ensure percentage is a valid number
    // percentage represents REMAINING BALANCE (not usage)
    // High balance (100%) = Good = Green
    // Low balance (0-50%) = Bad = Red
    const validPercentage = Number(percentage) || 0;
    
    if (validPercentage >= 90) {
      // High balance (90-100%) = Green (plenty left)
      return "linear-gradient(to right, #4caf50, rgb(186, 234, 127))";
    } else if (validPercentage >= 50) {
      // Moderate balance (50-90%) = Yellow (getting low)
      return "linear-gradient(to right, #fbc02d, rgb(248, 234, 175))";
    } else {
      // Low balance (0-50%) = Red (almost depleted)
      return "linear-gradient(to right, rgb(245, 75, 95), rgb(247, 190, 190))";
    }
  };

  // Show loading state
  if (loading) {
    return (
      <>
        <div className="header">{t("current_service_status")}</div>
        <div className="service-status" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "18px", color: "#666" }}>
            {t("loading") || "Loading..."}
          </div>
        </div>
      </>
    );
  }

  // Show message if no service selected or no data
  if (!planStatus) {
    return (
      <>
        <div className="header">{t("current_service_status")}</div>
        <div className="service-status" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "18px", color: "#666" }}>
            {t("no_data_available") || "No data available"}
          </div>
        </div>
      </>
    );
  }
  /*const calculateResults = (data) => {
    
    const planNames = data.map(entry => entry.planName).flat();
    
   const totalInitialCallMinutes = data.reduce((acc, entry) => acc + parseInt(entry.initialCallMinutes), 0);
   
   const totalInitialChats = data.reduce((acc, entry) => acc + parseInt(entry.initialChats), 0);
  
     const latestUsedCallMinutes = data.reduce((acc, entry) => {
     const latestDate = Object.keys(entry.usedCallMinutes).sort((a, b) => new Date(b) - new Date(a))[0];
    return acc + parseInt(entry.usedCallMinutes[latestDate]);
    }, 0);
    
    const latestUsedChats = data.reduce((acc, entry) => {
     const latestDate = Object.keys(entry.usedChats).sort((a, b) => new Date(b) - new Date(a))[0];
    return acc + parseInt(entry.usedChats[latestDate]);
     }, 0);
    
    const balanceCalls = totalInitialCallMinutes - latestUsedCallMinutes;
    const balanceChats = totalInitialChats - latestUsedChats;
   const callPercentage = (latestUsedCallMinutes / totalInitialCallMinutes) * 100;
  const chatPercentage = (latestUsedChats / totalInitialChats) * 100;
    const planStatusData = {
      name:planNames,
      totalCallMinutes:totalInitialCallMinutes,
      totalChats:totalInitialChats,
      connectedCallMinutes:latestUsedCallMinutes,
      deliveredChats:latestUsedChats,
      balanceCalls:balanceCalls,
      balanceChats:balanceChats,
      callPercentage:callPercentage,
      chatPercentage:chatPercentage
    }
    return planStatusData;
  
    
  }*/

  // Renders one card (plan name/calls/chats table + both balance sections) for a single
  // plan-shaped object. Used once per plan when a per-plan breakdown is available, or once
  // with the combined totals as a fallback — markup/classNames are unchanged from before,
  // just extracted so it can repeat per plan.
  const renderPlanCard = (plan, cardKey) => (
    <div className="service-status" key={cardKey} style={{ marginBottom: "16px" }}>
      {isMobile ? (
        // Mobile View
        <div className="mobile-view">
          {/* Titles Row */}
          <div className="mobile-row titles-row">
            <div className="mobile-title">
              {t("recharge_plan_name").toUpperCase()}
            </div>
            <div className="mobile-title">
              {t("total_connected_call_minutes").toUpperCase()}
            </div>
            <div className="mobile-title">
              {t("total_delivered_chats").toUpperCase()}
            </div>
          </div>
          {/* Values Row */}
          <div className="mobile-row values-row">
            <div className="mobile-value plan-name">
              {plan.planName || plan.planNames || "N/A"}
            </div>
            <div className="mobile-value">
              {plan.usedCallsForDealer || 0}/{plan.totalCallForDealer || 0} {t("min")}
            </div>
            <div className="mobile-value">
              {plan.usedChatsForDealer || 0}/{plan.totalChatsForDelaer || 0} {t("chats").toLowerCase()}
            </div>
          </div>
        </div>
      ) : (
        // Desktop View
        <div className="table desktop-view">
          <div className="row header-row">
            <div className="cell">{t("recharge_plan_name")}</div>
            <div className="cell">{t("total_connected_call_minutes")}</div>
            <div className="cell">{t("total_delivered_chats")}</div>
          </div>
          <div className="row" style={{ padding: "10px" }}>
            <div className="cell plan-name" style={{ fontSize: "16px" }}>
              {plan.planName || plan.planNames || "N/A"}
            </div>
            <div
              className="cell"
              style={{ fontSize: "16px", fontWeight: "bold" }}
            >
              {plan.usedCallsForDealer || 0}/{plan.totalCallForDealer || 0} {t("minutes")}
            </div>
            <div
              className="cell"
              style={{ fontSize: "16px", fontWeight: "bold" }}
            >
              {plan.usedChatsForDealer || 0}/{plan.totalChatsForDelaer || 0} {t("chats")}
            </div>
          </div>
        </div>
      )}

      <div className="section-separator"></div>

      {/* Call Minutes Progress Section */}
      <div className="balance">
        <div className="balance-header">{t("balance_call")}</div>
        <div className="balance-texts">
          <span style={{ fontWeight: "bold", color: "black" }}>
            {plan.balanceCallsForDealer || 0}
          </span>{" "}
          <span style={{ color: "black", fontWeight: "bold" }}>
            {t("minutes")}
          </span>{" "}
          {t("out_of_total")}
          <span style={{ fontWeight: "bold", color: "black" }}>
            {t("minutes_left", {
              total: plan.totalCallForDealer || 0
            })}
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress"
            style={{
              width: `${plan.callBalancePercentage || 0}%`,
              background: getProgressBarColor(plan.callBalancePercentage || 0),
              minWidth: plan.callBalancePercentage > 0 ? "2px" : "0px", // Ensure visibility if > 0
            }}
          ></div>
        </div>
        <div className="progress-labels">
          <span>0 {t("minutes")}</span>
          <span>
            {plan.totalCallForDealer || 0} {t("minutes")}
          </span>
        </div>
      </div>

      <div className="section-separator"></div>

      {/* Chats Progress Section */}
      <div className="balance">
        <div className="balance-header">{t("balance_chats")}</div>
        <div className="balance-texts">
          <span style={{ fontWeight: "bold", color: "black" }}>
            {plan.balanceChatsForDealer || 0}{" "}
          </span>{" "}
          <span style={{ color: "black", fontWeight: "bold" }}>
            {t("chats")}
          </span>{" "}
          {t("out_of_total")}
          <span style={{ fontWeight: "bold", color: "black" }}>
            {t("chats_left", {
              total: plan.totalChatsForDelaer || 0
            })}
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress"
            style={{
              width: `${plan.chatBalancePercentage || 0}%`,
              background: getProgressBarColor(plan.chatBalancePercentage || 0),
              minWidth: plan.chatBalancePercentage > 0 ? "2px" : "0px", // Ensure visibility if > 0
            }}
          ></div>
        </div>
        <div className="progress-labels">
          <span>0 {t("chats")}</span>
          <span>
            {plan.totalChatsForDelaer || 0} {t("chats")}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="header">{t("current_service_status")}</div>
      <div className="service-status-cards-scroll">
        {Array.isArray(planStatus.planBalances) && planStatus.planBalances.length > 0
          ? planStatus.planBalances.map((plan, index) =>
              renderPlanCard(plan, plan.planId || index)
            )
          : renderPlanCard(planStatus, "combined")}
      </div>
    </>
  );
};

export default ServiceStatus;
